import type { TaskError } from './types';

export type TaskType =
  | 'thumbnail_generation'
  | 'listing_generation'
  | 'design_check'
  | 'field_generation'
  | 'listing_check';
export type TaskEntityType = 'design' | 'listing' | 'batch';

export interface TaskState {
  taskId: string;
  taskType: TaskType;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  progressMessage: string;
  entityType: TaskEntityType;
  entityId: string;
  createdAt: Date;
  startedAt?: Date | undefined;
  completedAt?: Date | undefined;
  displayName?: string | undefined;
  payload?: Record<string, unknown> | undefined;
  result?: Record<string, unknown> | undefined;
  error?: TaskError | undefined;
  attempts?: number | undefined;
  /** Stable within one execution, including late enrichment of an async response. */
  executionKey?: string | undefined;
  batchProgress?: { total: number; completed: number; failed: number } | undefined;
}

export interface TaskManagerState {
  activeTasks: Map<string, TaskState>;
  recentCompleted: TaskState[];
  unseenCount: number;
}

interface ExecutionRecord {
  generation: number;
  task: TaskState;
  executionObservedAt: number;
  terminalObservedAt?: number;
  notificationPending: boolean;
  /** Worker claim markers survive automatic queue retries that clear started_at. */
  claimedExecution?: Pick<TaskState, 'attempts' | 'startedAt'>;
  /** Retain the old execution markers while a manual retry is still pending. */
  previousTerminal?: TaskState;
}

export interface TaskIngestionOptions {
  /** Reserve the terminal event while authoritative details are being fetched. */
  notify?: boolean;
  /** Request start time, used only to reject stale discovery after a terminal event. */
  requestStartedAt?: number;
  observedAt?: number;
  /** An async hydration response belongs only to the execution that requested it. */
  expectedExecutionKey?: string;
  /** Only set after the API confirms cancellation of this captured execution. */
  confirmedCancellation?: boolean;
}

export interface TaskIngestionResult {
  state: TaskManagerState;
  changed: boolean;
  terminalEvent?: TaskState;
}

export function isTerminalTaskStatus(status: TaskState['status']): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

export function getTaskExecutionKey(task: TaskState): string {
  return (
    task.executionKey ??
    `${task.taskId}:${task.attempts ?? 0}:${task.startedAt?.toISOString() ?? task.createdAt.toISOString()}`
  );
}

/** Synchronous session state; callers publish snapshots to React only after ingestion. */
export class TaskLifecycleStore {
  state: TaskManagerState = { activeTasks: new Map(), recentCompleted: [], unseenCount: 0 };
  private readonly records = new Map<string, ExecutionRecord>();
  private readonly unseen = new Set<string>();

  constructor(
    private readonly maxRecentCompleted = 50,
    private readonly maxDedupeEntries = 500
  ) {}

  ingest(input: TaskState, options: TaskIngestionOptions = {}): TaskIngestionResult {
    const previous = this.records.get(input.taskId);
    if (
      options.expectedExecutionKey !== undefined &&
      previous?.task.executionKey !== options.expectedExecutionKey
    ) {
      return { state: this.state, changed: false };
    }
    const previousTerminal = previous && isTerminalTaskStatus(previous.task.status);
    const laterStart = !!(
      input.startedAt &&
      previous?.task.startedAt &&
      input.startedAt > previous.task.startedAt
    );
    const higherAttempt =
      input.attempts !== undefined &&
      previous?.task.attempts !== undefined &&
      input.attempts > previous.task.attempts;
    const freshActiveRetry =
      previousTerminal &&
      previous.task.status !== 'completed' &&
      (input.status === 'pending' ||
        (input.status === 'processing' &&
          (previous.task.startedAt === undefined || previous.task.attempts === undefined) &&
          (input.startedAt !== undefined || input.attempts !== undefined))) &&
      options.requestStartedAt !== undefined &&
      previous.terminalObservedAt !== undefined &&
      options.requestStartedAt > previous.terminalObservedAt;
    const newExecution = !!previousTerminal && (laterStart || higherAttempt || freshActiveRetry);

    const fence = previous?.previousTerminal;
    if (fence && !previousTerminal && input.status !== 'pending') {
      const newerThanFence =
        (input.attempts !== undefined &&
          fence.attempts !== undefined &&
          input.attempts > fence.attempts) ||
        (input.startedAt && fence.startedAt && input.startedAt > fence.startedAt);
      const matchesClaimedExecution =
        previous.claimedExecution !== undefined &&
        ((fence.attempts === undefined &&
          input.attempts !== undefined &&
          previous.claimedExecution.attempts !== undefined &&
          input.attempts >= previous.claimedExecution.attempts) ||
          (fence.startedAt === undefined &&
            input.startedAt &&
            previous.claimedExecution.startedAt &&
            input.startedAt >= previous.claimedExecution.startedAt));
      const freshWorkerClaim =
        input.status === 'processing' &&
        options.requestStartedAt !== undefined &&
        options.requestStartedAt > previous.executionObservedAt &&
        ((fence.attempts === undefined && input.attempts !== undefined) ||
          (fence.startedAt === undefined && input.startedAt !== undefined));
      const freshUnstartedTerminal =
        isTerminalTaskStatus(input.status) &&
        input.startedAt === undefined &&
        options.expectedExecutionKey === previous.task.executionKey &&
        options.requestStartedAt !== undefined &&
        options.requestStartedAt > previous.executionObservedAt;
      const confirmedCancellation =
        input.status === 'cancelled' &&
        options.confirmedCancellation === true &&
        options.expectedExecutionKey === previous.task.executionKey;
      // A reset clears started_at without incrementing attempts. A late result
      // from before that reset must not finish the newly accepted retry. A new
      // authoritative read can still report failure/cancellation before claim.
      if (
        !newerThanFence &&
        !matchesClaimedExecution &&
        !freshWorkerClaim &&
        !freshUnstartedTerminal &&
        !confirmedCancellation
      )
        return { state: this.state, changed: false };
    }

    // An old discovery/creation snapshot must not resurrect a finished execution.
    if (previousTerminal && !newExecution && !isTerminalTaskStatus(input.status)) {
      return { state: this.state, changed: false };
    }
    if (previous && !newExecution) {
      const olderAttempt =
        input.attempts !== undefined &&
        previous.task.attempts !== undefined &&
        input.attempts < previous.task.attempts;
      const olderStart =
        input.startedAt && previous.task.startedAt && input.startedAt < previous.task.startedAt;
      if (olderAttempt || olderStart) return { state: this.state, changed: false };
    }

    const generation = (previous?.generation ?? 0) + (newExecution ? 1 : 0);
    // Optional metadata can be absent from creation/partial polling responses.
    // Explicitly cleared execution timestamps still describe a server retry reset.
    const definedInput = Object.fromEntries(
      Object.entries(input).filter(
        ([key, value]) => value !== undefined || key === 'startedAt' || key === 'completedAt'
      )
    ) as TaskState;
    const task: TaskState = {
      ...(previous && !newExecution ? previous.task : {}),
      ...definedInput,
      executionKey: `${input.taskId}:${generation}`,
    };
    const record: ExecutionRecord =
      newExecution || !previous
        ? {
            generation,
            task,
            executionObservedAt: options.observedAt ?? Date.now(),
            notificationPending: false,
            ...(newExecution && previous ? { previousTerminal: previous.task } : {}),
          }
        : { ...previous, task };
    if (task.status === 'processing') {
      record.claimedExecution = { attempts: task.attempts, startedAt: task.startedAt };
    }
    const activeTasks = new Map(this.state.activeTasks);
    let recentCompleted = this.state.recentCompleted;

    if (newExecution) {
      recentCompleted = recentCompleted.filter((item) => item.taskId !== task.taskId);
      this.unseen.delete(task.taskId);
    }

    let terminalEvent: TaskState | undefined;
    if (isTerminalTaskStatus(task.status)) {
      activeTasks.delete(task.taskId);
      if (!previousTerminal || newExecution) {
        record.terminalObservedAt = options.observedAt ?? Date.now();
        record.notificationPending = true;
        recentCompleted = [
          task,
          ...recentCompleted.filter((item) => item.taskId !== task.taskId),
        ].slice(0, this.maxRecentCompleted);
        this.unseen.add(task.taskId);
      } else {
        // Enrich the existing event, but never restore an event the user dismissed.
        recentCompleted = recentCompleted.map((item) =>
          item.taskId === task.taskId ? task : item
        );
      }
      if (record.notificationPending && options.notify !== false) {
        record.notificationPending = false;
        terminalEvent = task;
      }
    } else {
      activeTasks.set(task.taskId, task);
    }

    // Reserve dedupe synchronously, before the caller performs toast or analytics effects.
    // Updating an existing ID also refreshes its eviction order after a retry.
    this.records.delete(task.taskId);
    this.records.set(task.taskId, record);
    const retained = new Set(recentCompleted.map((item) => item.taskId));
    this.pruneRecords(retained, task.taskId);
    for (const taskId of this.unseen) if (!retained.has(taskId)) this.unseen.delete(taskId);
    this.state = { activeTasks, recentCompleted, unseenCount: this.unseen.size };
    return { state: this.state, changed: true, ...(terminalEvent ? { terminalEvent } : {}) };
  }

  removeTask(taskId: string): TaskManagerState {
    const activeTasks = new Map(this.state.activeTasks);
    activeTasks.delete(taskId);
    this.state = { ...this.state, activeTasks };
    return this.state;
  }

  markSeen(taskIds: readonly string[]): TaskManagerState {
    for (const taskId of taskIds) this.unseen.delete(taskId);
    this.state = { ...this.state, unseenCount: this.unseen.size };
    return this.state;
  }

  markAllSeen(): TaskManagerState {
    return this.markSeen([...this.unseen]);
  }

  dismissTask(taskId: string): TaskManagerState {
    this.unseen.delete(taskId);
    this.state = {
      ...this.state,
      recentCompleted: this.state.recentCompleted.filter((item) => item.taskId !== taskId),
      unseenCount: this.unseen.size,
    };
    return this.state;
  }

  clearRecentCompleted(): TaskManagerState {
    this.unseen.clear();
    this.state = { ...this.state, recentCompleted: [], unseenCount: 0 };
    return this.state;
  }

  private pruneRecords(retained: ReadonlySet<string>, currentTaskId: string): void {
    for (const [taskId, record] of this.records) {
      if (this.records.size <= this.maxDedupeEntries) break;
      if (
        taskId !== currentTaskId &&
        isTerminalTaskStatus(record.task.status) &&
        !record.notificationPending &&
        !retained.has(taskId)
      )
        this.records.delete(taskId);
    }
  }
}
